import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import {
  Mail, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2, XCircle,
  ChevronDown, ChevronRight, Send, Inbox, Info, X, Save, RefreshCw,
} from 'lucide-react';

// Settings > Mailboxes.
//
// One card per configured mailbox, each with its own SMTP (sending) and IMAP
// (receiving) credentials. Before this screen the whole ATS shared a single
// SMTP account held in Settings and had no IMAP credential at all, which is why
// the shared inbox could send but never pull.
//
// Two rules this component must not break:
//   1. A password is never displayed. The server sends has_smtp_password /
//      has_imap_password booleans and nothing else, and a blank password field
//      on save means "keep whatever is stored".
//   2. Visibility is decided on the server. Hiding the Add button for a
//      read-only role is a courtesy, not the control — the API refuses them too.

const FALLBACK_DEFAULTS = {
  smtp_host: 'smtp.gmail.com', smtp_port: 587,
  imap_host: 'imap.gmail.com', imap_port: 993,
};

// Only used if the server sends no role list (it normally does, built from the
// roles that actually exist on user rows).
const FALLBACK_ROLES = [
  'super_admin', 'admin', 'owner', 'chief_of_staff',
  'hr_manager', 'recruiter', 'office_admin', 'employee',
];

const ALWAYS_ALLOWED = ['super_admin', 'admin', 'owner', 'chief_of_staff'];

const roleLabel = (r) => r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const errText = (e) => e?.response?.data?.detail || e?.message || String(e);

// Grey covers two different situations — never configured, and configured but
// never tested — so the tooltip carries the difference the colour cannot.
const dotFor = (mailbox, fresh, leg) => {
  const status = fresh ? fresh[leg] : mailbox[`${leg}_status`];
  const configured = leg === 'smtp' ? mailbox.has_smtp_password : mailbox.has_imap_password;
  if (status && typeof status.ok === 'boolean') {
    return status.ok
      ? { cls: 'bg-emerald-500', title: status.detail || 'Tested OK' }
      : { cls: 'bg-red-500', title: status.detail || 'Last test failed' };
  }
  return configured
    ? { cls: 'bg-slate-300', title: 'Password saved — not tested yet' }
    : { cls: 'bg-slate-300', title: 'Not configured' };
};

function StatusDot({ mailbox, fresh, leg, label }) {
  const dot = dotFor(mailbox, fresh, leg);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" title={dot.title}>
      <span className={`w-2 h-2 rounded-full ${dot.cls}`} data-testid={`dot-${leg}`} />
      {label}
    </span>
  );
}

function GmailHelp() {
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-xs leading-relaxed">
      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <span>
        Gmail and Google Workspace will never accept the normal account password here.
        Switch on <strong>2-Step Verification</strong> for the account, then create an{' '}
        <strong>App Password</strong> (Google Account &rarr; Security &rarr; App passwords)
        and paste the 16-character value. For receiving, IMAP also has to be enabled —
        Gmail &rarr; Settings &rarr; Forwarding and POP/IMAP, and on Workspace an
        administrator enables it under Apps &rarr; Google Workspace &rarr; Gmail &rarr;
        End User Access.
      </span>
    </div>
  );
}

function TextField({ label, hint, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      <input
        {...props}
        className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white
                   text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400"
      />
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

function Advanced({ title, icon: Icon, open, onToggle, children }) {
  return (
    <div className="border border-slate-200 rounded-lg">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Icon className="w-3.5 h-3.5" />
        {title}
        <span className="ml-auto text-[11px] font-normal text-slate-400">
          {open ? 'Hide' : 'Server settings'}
        </span>
      </button>
      {open && <div className="px-3 pb-3 pt-1 grid gap-3 sm:grid-cols-3">{children}</div>}
    </div>
  );
}

// --------------------------------------------------------------------------
// The add / edit form
// --------------------------------------------------------------------------

const blankForm = (defaults) => ({
  id: '',
  email: '',
  display_name: '',
  role: '',
  visible_to: [],
  active: true,
  smtp_host: defaults.smtp_host,
  smtp_port: String(defaults.smtp_port),
  smtp_username: '',
  imap_host: defaults.imap_host,
  imap_port: String(defaults.imap_port),
  imap_username: '',
  smtp_password: '',
  imap_password: '',
  // On Gmail the same App Password serves both legs, and typing it twice is how
  // one of them ends up with a typo nobody notices until mail stops arriving.
  same_password: true,
});

const formFrom = (mailbox, defaults) => ({
  ...blankForm(defaults),
  id: mailbox.id || '',
  email: mailbox.email || '',
  display_name: mailbox.display_name || '',
  role: mailbox.role || '',
  visible_to: [...(mailbox.visible_to || [])],
  active: mailbox.active !== false,
  smtp_host: mailbox.smtp_host || defaults.smtp_host,
  smtp_port: String(mailbox.smtp_port || defaults.smtp_port),
  smtp_username: mailbox.smtp_username || '',
  imap_host: mailbox.imap_host || defaults.imap_host,
  imap_port: String(mailbox.imap_port || defaults.imap_port),
  imap_username: mailbox.imap_username || '',
});

function MailboxForm({ initial, roles, defaults, saving, error, onSave, onCancel }) {
  const [form, setForm] = useState(() =>
    (initial ? formFrom(initial, defaults) : blankForm(defaults)));
  const [showSmtp, setShowSmtp] = useState(false);
  const [showImap, setShowImap] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const toggleRole = (role) => setForm((f) => ({
    ...f,
    visible_to: f.visible_to.includes(role)
      ? f.visible_to.filter((r) => r !== role)
      : [...f.visible_to, role],
  }));

  const submit = (e) => {
    e.preventDefault();
    const port = (v) => (String(v).trim() === '' ? undefined : Number(v));
    const body = {
      id: form.id || undefined,
      email: form.email.trim(),
      display_name: form.display_name.trim(),
      role: form.role.trim(),
      visible_to: form.visible_to,
      active: form.active,
      smtp_host: form.smtp_host.trim(),
      smtp_port: port(form.smtp_port),
      smtp_username: form.smtp_username.trim(),
      imap_host: form.imap_host.trim(),
      imap_port: port(form.imap_port),
      imap_username: form.imap_username.trim(),
    };
    // Omitted entirely rather than sent blank: on the server a blank password
    // means "keep the stored one", and sending one anyway is a way to lose it.
    if (form.smtp_password) body.smtp_password = form.smtp_password;
    const imapPassword = form.same_password ? form.smtp_password : form.imap_password;
    if (imapPassword) body.imap_password = imapPassword;
    onSave(body);
  };

  const savedSmtp = initial?.has_smtp_password;
  const savedImap = initial?.has_imap_password;
  const roleList = roles.length ? roles : FALLBACK_ROLES;

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-slate-200 rounded-xl p-4 space-y-4"
      data-testid="mailbox-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">
          {initial ? `Edit ${initial.email}` : 'Add a mailbox'}
        </h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <GmailHelp />

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          label="Email address" type="email" required autoComplete="off"
          placeholder="hr@daruwallas.com"
          value={form.email} onChange={set('email')}
          data-testid="mailbox-email"
        />
        <TextField
          label="Display name" placeholder="DCS Recruitment"
          hint="The name recipients see on mail sent from this address."
          value={form.display_name} onChange={set('display_name')}
          data-testid="mailbox-display-name"
        />
      </div>

      <TextField
        label="Purpose (optional)" placeholder="hr_mailbox"
        hint="A short label for what this mailbox is for. Not a user role."
        value={form.role} onChange={set('role')}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">
              App Password for sending
            </span>
            <input
              type="password" autoComplete="new-password"
              value={form.smtp_password} onChange={set('smtp_password')}
              placeholder={savedSmtp ? 'Saved — leave blank to keep it' : 'abcd efgh ijkl mnop'}
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white
                         text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400"
              data-testid="mailbox-smtp-password"
            />
          </label>
          <label className="flex items-center gap-2 mt-2 text-xs text-slate-600">
            <input
              type="checkbox" checked={form.same_password} onChange={set('same_password')}
              className="rounded border-slate-300"
            />
            Use the same password for receiving (normal for Gmail)
          </label>
        </div>

        {!form.same_password && (
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">
              App Password for receiving
            </span>
            <input
              type="password" autoComplete="new-password"
              value={form.imap_password} onChange={set('imap_password')}
              placeholder={savedImap ? 'Saved — leave blank to keep it' : 'abcd efgh ijkl mnop'}
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white
                         text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400"
              data-testid="mailbox-imap-password"
            />
          </label>
        )}
      </div>

      <div>
        <span className="block text-xs font-medium text-slate-600 mb-1">Who can see this mailbox</span>
        <p className="text-[11px] text-slate-400 mb-2">
          Admins, owners and the chief of staff always see every mailbox — you do not need to tick those.
        </p>
        <div className="flex flex-wrap gap-2" data-testid="mailbox-visible-to">
          {roleList.map((role) => {
            const forced = ALWAYS_ALLOWED.includes(role);
            const checked = forced || form.visible_to.includes(role);
            return (
              <label
                key={role}
                title={forced ? 'This role always has access' : undefined}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border cursor-pointer ${
                  checked ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'
                } ${forced ? 'opacity-60 cursor-default' : ''}`}
              >
                <input
                  type="checkbox" className="sr-only"
                  checked={checked} disabled={forced}
                  onChange={() => toggleRole(role)}
                />
                {roleLabel(role)}
              </label>
            );
          })}
        </div>
      </div>

      <Advanced
        title="Sending (SMTP)" icon={Send}
        open={showSmtp} onToggle={() => setShowSmtp((v) => !v)}
      >
        <TextField label="Host" value={form.smtp_host} onChange={set('smtp_host')} />
        <TextField
          label="Port" inputMode="numeric" value={form.smtp_port} onChange={set('smtp_port')}
          hint="587 = STARTTLS, 465 = direct TLS"
        />
        <TextField
          label="Username" autoComplete="off" placeholder="same as the email address"
          value={form.smtp_username} onChange={set('smtp_username')}
        />
      </Advanced>

      <Advanced
        title="Receiving (IMAP)" icon={Inbox}
        open={showImap} onToggle={() => setShowImap((v) => !v)}
      >
        <TextField label="Host" value={form.imap_host} onChange={set('imap_host')} />
        <TextField
          label="Port" inputMode="numeric" value={form.imap_port} onChange={set('imap_port')}
          hint="993 for Gmail"
        />
        <TextField
          label="Username" autoComplete="off" placeholder="same as the email address"
          value={form.imap_username} onChange={set('imap_username')}
        />
      </Advanced>

      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox" checked={form.active} onChange={set('active')}
          className="rounded border-slate-300"
        />
        Active — the ATS may send and receive through this mailbox
      </label>

      {error && (
        <div className="flex items-start gap-2 p-3 text-xs text-red-600 bg-red-50 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit" disabled={saving}
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white
                     hover:bg-slate-700 disabled:opacity-40"
          data-testid="mailbox-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : 'Save mailbox'}
        </button>
        <button
          type="button" onClick={onCancel} disabled={saving}
          className="text-xs px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// --------------------------------------------------------------------------
// One leg of a test result, in plain English
// --------------------------------------------------------------------------

function TestLeg({ label, result }) {
  if (!result) return null;
  const Icon = result.ok ? CheckCircle2 : XCircle;
  return (
    <div className={`flex items-start gap-2 text-xs ${result.ok ? 'text-emerald-700' : 'text-red-600'}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-px" />
      <span>
        <strong>{label}:</strong> {result.detail}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------
// The panel
// --------------------------------------------------------------------------

export default function MailboxesPanel() {
  const [mailboxes, setMailboxes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [defaults, setDefaults] = useState(FALLBACK_DEFAULTS);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null);   // null | 'new' | a mailbox object
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [testing, setTesting] = useState('');     // id of the mailbox being tested
  const [results, setResults] = useState({});     // id -> { smtp, imap }
  const [confirmDelete, setConfirmDelete] = useState('');
  const [deleting, setDeleting] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/mailbox-config');
      setMailboxes(r.data.mailboxes || []);
      setRoles(r.data.roles || []);
      setCanWrite(!!r.data.can_write);
      if (r.data.defaults) setDefaults(r.data.defaults);
      setError('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setSaving(true);
    setFormError('');
    try {
      await api.post('/mailbox-config', body);
      setEditing(null);
      // Drop this session's test results: they describe credentials that may
      // have just changed, and a dot that stays green through a password change
      // is worse than no dot at all.
      setResults({});
      await load();
    } catch (e) {
      setFormError(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async (id) => {
    setTesting(id);
    setError('');
    try {
      const r = await api.post(`/mailbox-config/${id}/test`);
      setResults((prev) => ({ ...prev, [id]: r.data }));
    } catch (e) {
      setError(errText(e));
    } finally {
      setTesting('');
    }
  };

  const remove = async (id) => {
    setDeleting(id);
    setError('');
    try {
      await api.delete(`/mailbox-config/${id}`);
      setConfirmDelete('');
      setResults((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setDeleting('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading mailboxes…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mailboxes-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-700" /> Mailboxes
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Each mailbox carries its own credentials for sending and for receiving. Roles
            you tick below can see that mailbox in the shared inbox; admins, owners and the
            chief of staff always see all of them.
          </p>
        </div>
        {canWrite && !editing && (
          <button
            onClick={() => { setFormError(''); setEditing('new'); }}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700"
            data-testid="mailbox-add"
          >
            <Plus className="w-3.5 h-3.5" /> Add mailbox
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 text-xs text-red-600 bg-red-50 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {editing && (
        <MailboxForm
          // Keyed on the target so React remounts the form when the admin jumps
          // straight from editing one mailbox to editing another — without this
          // the second mailbox opens showing the first one's values.
          key={editing === 'new' ? 'new' : editing.id}
          initial={editing === 'new' ? null : editing}
          roles={roles}
          defaults={defaults}
          saving={saving}
          error={formError}
          onSave={save}
          onCancel={() => { setEditing(null); setFormError(''); }}
        />
      )}

      {mailboxes.length === 0 && !editing && (
        <div className="border border-dashed border-slate-200 rounded-xl py-12 text-center">
          <p className="text-sm text-slate-500">No mailboxes configured yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            {canWrite
              ? 'Add one to let the ATS send and receive mail from that address.'
              : 'No mailbox has been shared with your role yet. Ask an admin.'}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {mailboxes.map((mb) => {
          const fresh = results[mb.id];
          const busy = testing === mb.id;
          return (
            <div
              key={mb.id}
              className="bg-white border border-slate-200 rounded-xl p-4"
              data-testid={`mailbox-card-${mb.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-900 truncate">{mb.email}</p>
                    {!mb.active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        Inactive
                      </span>
                    )}
                    {!mb.persisted && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Not saved yet
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {mb.display_name || 'No display name'}
                    {mb.role ? ` · ${mb.role}` : ''}
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <StatusDot mailbox={mb} fresh={fresh} leg="smtp" label="Sending" />
                    <StatusDot mailbox={mb} fresh={fresh} leg="imap" label="Receiving" />
                  </div>
                  {(mb.visible_to || []).length > 0 && (
                    <p className="text-[11px] text-slate-400 mt-2">
                      Also visible to: {mb.visible_to.map(roleLabel).join(', ')}
                    </p>
                  )}
                </div>

                {canWrite && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => test(mb.id)}
                      disabled={busy || !mb.persisted}
                      title={mb.persisted ? 'Sign in to this account and report back' : 'Save the mailbox first'}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-slate-200
                                 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                      data-testid={`mailbox-test-${mb.id}`}
                    >
                      {busy
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCw className="w-3.5 h-3.5" />}
                      {busy ? 'Testing…' : 'Test connection'}
                    </button>
                    <button
                      onClick={() => { setFormError(''); setEditing(mb); }}
                      className="text-xs px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                    {mb.persisted && (
                      <button
                        onClick={() => setConfirmDelete(mb.id)}
                        className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50"
                        title="Remove this mailbox"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {confirmDelete === mb.id && (
                <div className="mt-3 p-3 rounded-lg bg-red-50 text-xs text-red-700 flex items-center gap-3 flex-wrap">
                  <span>
                    Remove {mb.email}? Mail already pulled in stays where it is; only the
                    credentials and the sharing settings go.
                  </span>
                  <button
                    onClick={() => remove(mb.id)}
                    disabled={deleting === mb.id}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 text-white disabled:opacity-40"
                  >
                    {deleting === mb.id && <Loader2 className="w-3 h-3 animate-spin" />}
                    Remove
                  </button>
                  <button
                    onClick={() => setConfirmDelete('')}
                    className="px-2.5 py-1.5 rounded-lg border border-red-200 text-red-700"
                  >
                    Keep it
                  </button>
                </div>
              )}

              {fresh && (
                <div className="mt-3 p-3 rounded-lg bg-slate-50 space-y-1.5" data-testid={`mailbox-result-${mb.id}`}>
                  <TestLeg label="Sending" result={fresh.smtp} />
                  <TestLeg label="Receiving" result={fresh.imap} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {mailboxes.length > 0 && canWrite && <GmailHelp />}
    </div>
  );
}
